/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { CultureStore } from 'src/core/stores/CultureStore';
import { getOrDefaultRegionFromResource, isPredictionResource, renderPredictionCsAccounts } from 'src/core/utils/csAccountUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

export type ConversationAppCreateModalSubmitDto = ConversationAppCreateModalDto & { csAccountStore: AzureCsAccountStore };

export type ConversationAppCreateModalProps = BasicModalProps<ConversationAppCreateModalSubmitDto> & {
    /**
     * An array of cultures supported in the application.
     */
    cultures: CultureStore[];

    /**
     * A flag to indicate whether cultures are loading or not.
     */
    areCulturesLoading: boolean;
};

export type ConversationAppCreateModalDto = {
    /**
     * The value of the name field.
     */
    name: string;

    /**
     * The value of the description field.
     */
    description: string;

    /**
     * The culture code from the culture dropdown.
     */
    culture: string;

    /**
     * The Cs Account Azure path.
     */
    csAccountPath: string;
};

const messages = defineMessages({
    modalTitle: {
        id: 'apps.conversationAppCreateModal.modalTitle',
        defaultMessage: 'Create new app'
    },
    nameLabel: {
        id: 'apps.conversationAppCreateModal.nameLabel',
        defaultMessage: 'Name'
    },
    namePlaceholder: {
        id: 'apps.conversationAppCreateModal.namePlaceholder',
        defaultMessage: 'Type app name ...'
    },
    cultureLabel: {
        id: 'apps.conversationAppCreateModal.cultureLabel',
        defaultMessage: 'Culture'
    },
    culturePlaceholder: {
        id: 'apps.conversationAppCreateModal.culturePlaceholder',
        defaultMessage: 'Select app culture ...'
    },
    cultureNotification: {
        id: 'apps.conversationAppCreateModal.cultureNotification',
        defaultMessage: 'Culture is the language that your app understands, not the interface language.'
    },
    descriptionLabel: {
        id: 'apps.conversationAppCreateModal.descriptionLabel',
        defaultMessage: 'Description'
    },
    descriptionPlaceholder: {
        id: 'apps.conversationAppCreateModal.descriptionPlaceholder',
        defaultMessage: 'Type app description ...'
    },
    csAccountLabel: {
        id: 'apps.conversationAppCreateModal.csAccountLabel',
        defaultMessage: 'Prediction resource'
    },
    csAccountPlaceholder: {
        id: 'apps.conversationAppCreateModal.csAccountPlaceholder',
        defaultMessage: 'Choose LUIS Cognitive Service Resource for predictions ...'
    },
    predictionResourceDescription: {
        id: 'apps.conversationAppCreateModal.predictionResourceDescription',
        defaultMessage: 'After you publish your LUIS application, use the prediction resource/key to query prediction endpoint requests.'
    },
    learnMoreDetailed: {
        id: 'apps.conversationAppCreateModal.learnMoreDetailed',
        defaultMessage: 'Learn more about prediction resources.'
    }
});

/**
 * Represents a modal to create a new app.
 *
 * @param props The properties passed to the modal.
 */
export const ConversationAppCreateModal = withModalMounter(
    observer((props: ConversationAppCreateModalProps) => {
        const { cultures, areCulturesLoading, onSubmit } = props;

        const { localize } = useLocalization();
        const { rootStore, dispatcher } = useGlobalState();
        const azureSession = rootStore.azureStore.session;
        const { formState, formStateDto, updateFormState } = useFormState<ConversationAppCreateModalDto>({
            name: ['', FormValidators.createRequiredValidator(), FormValidators.createBlacklistValidator()],
            culture: ['en-us', FormValidators.createRequiredValidator()],
            description: [''],
            csAccountPath: ['']
        });

        const csAccountsPerSubscriptionStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
        const predictionCsAccounts = csAccountsPerSubscriptionStoreHouse.items.filter(isPredictionResource);
        const selectedCsAccount = predictionCsAccounts.find(c => c.path === formState.fields.csAccountPath.value);

        const cultureElementId = React.useRef(getId('cultureElementId')).current;
        const predictionResourceElementId = React.useRef(getId('predictionResourceElementId')).current;

        React.useEffect(() => {
            if (azureSession.subscriptionId) {
                dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
                    id: azureSession.subscriptionId,
                    localStoreHouse: csAccountsPerSubscriptionStoreHouse
                });
            }
        }, [azureSession.subscriptionId]);

        const renderLabelWithDescription = (
            label: string,
            description: string,
            htmlFor: string,
            required: boolean,
            learnMoreLink: string = null
        ) => (
            <Stack horizontal verticalAlign="center">
                <Label required={required} htmlFor={htmlFor}>
                    {label}
                </Label>

                <CalloutButton
                    content={
                        <LuisText variant="small">
                            {description}
                            &nbsp;
                            {learnMoreLink && (
                                <ExternalLink isDarkMode aria-label={localize(messages.learnMoreDetailed)} href={learnMoreLink}>
                                    {localize(globalMessages.learnMore)}
                                </ExternalLink>
                            )}
                        </LuisText>
                    }
                />
            </Stack>
        );

        return (
            <BasicModal
                {...props}
                valid={formState.form.valid}
                dialogContentProps={{ title: localize(messages.modalTitle) }}
                onSubmit={() => onSubmit({ ...formStateDto, csAccountStore: selectedCsAccount })}>
                <Stack tokens={{ childrenGap: 20 }}>
                    <LuisTextField
                        required
                        autoFocus
                        value={formState.fields.name.value}
                        label={localize(messages.nameLabel)}
                        data-automation-id="conversationAppNameInput"
                        placeholder={localize(messages.namePlaceholder)}
                        errorMessage={formState.fields.name.errorMessage}
                        onChange={(_, name) => updateFormState({ name })}
                        onEnterKeyUp={() => formState.form.valid && onSubmit({ ...formStateDto, csAccountStore: selectedCsAccount })}
                    />

                    <LuisDropdown
                        maxHeight={200}
                        isLoading={areCulturesLoading}
                        data-automation-id="appCultures"
                        aria-labelledby={cultureElementId}
                        selectedKey={formState.fields.culture.value}
                        placeholder={localize(messages.culturePlaceholder)}
                        errorMessage={formState.fields.culture.errorMessage}
                        options={cultures.map(c => ({ key: c.code, text: c.name }))}
                        onChange={(_, opt) => updateFormState({ culture: opt.key as string })}
                        ariaLabel={`${localize(messages.cultureLabel)} ${localize(messages.cultureNotification)}`}
                        onRenderLabel={() =>
                            renderLabelWithDescription(
                                localize(messages.cultureLabel),
                                localize(messages.cultureNotification),
                                cultureElementId,
                                true
                            )
                        }
                    />

                    <LuisTextField
                        multiline
                        data-automation-id="AppDescription"
                        value={formState.fields.description.value}
                        label={localize(messages.descriptionLabel)}
                        placeholder={localize(messages.descriptionPlaceholder)}
                        errorMessage={formState.fields.description.errorMessage}
                        onChange={(_, description) => updateFormState({ description })}
                    />

                    <LuisDropdown
                        selectedKey={selectedCsAccount?.path}
                        data-automation-id="predictionResourceId"
                        aria-labelledby={predictionResourceElementId}
                        ariaLabel={localize(messages.csAccountLabel)}
                        placeholder={localize(messages.csAccountPlaceholder)}
                        isLoading={csAccountsPerSubscriptionStoreHouse.tracker.isRunning}
                        onChange={(_, opt) => updateFormState({ csAccountPath: opt.key as string })}
                        options={renderPredictionCsAccounts(
                            predictionCsAccounts,
                            getOrDefaultRegionFromResource(azureSession.csAccountStore)
                        )}
                        onRenderLabel={() =>
                            renderLabelWithDescription(
                                localize(messages.csAccountLabel),
                                localize(messages.predictionResourceDescription),
                                predictionResourceElementId,
                                false,
                                'https://docs.microsoft.com/azure/cognitive-services/luis/luis-how-to-azure-subscription#luis-resources'
                            )
                        }
                    />
                </Stack>
            </BasicModal>
        );
    })
);
